import { useCallback, useEffect, useMemo } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { startOfDay } from 'date-fns';
import { useAtomValue } from 'jotai';

import type {
  TBatchFindingsResponse,
  TTreatmentResponse,
} from '@/modules/claims/freeClaims/insuranceExport/types';
import { searchAtom, selectDateAtom } from '@/modules/patientList/atoms';
import { endpoints } from '@/react-query/endpoints';
import { useBatchNotesByStandalonePatientVisitIds } from '@/react-query/videa-notes/useBatchNotesByStandalonePatientVisitIds';
import { useBatchNotesByUnifiedAppointmentIds } from '@/react-query/videa-notes/useBatchNotesByUnifiedAppointmentIds';
import type {
  VoiceNotesSearchStandaloneVisit,
  VoiceNotesSearchUnifiedAppointment,
} from '@/react-query/videa-notes/useCombinedAppointments';
import { useCombinedAppointments } from '@/react-query/videa-notes/useCombinedAppointments';
import type { ClinicalNote } from '@/react-query/videa-notes/useExistingNotes';
import type { BatchFindingForVoiceNotes } from '@/react-query/videa-notes/useTreatmentsBatch';
import { useTreatmentsBatch } from '@/react-query/videa-notes/useTreatmentsBatch';
import { useDebounce } from '@/utils/hooks/useDebounce';

/**
 * Fetches and combines ambient data for appointments including:
 * - Appointments for a selected date range with optional patient name search
 * - Treatment data for each appointment via batch queries
 * - Clinical notes for each appointment via batch queries
 *
 * Returns appointments enriched with their associated treatments and notes,
 * along with loading states and pagination controls.
 */
export const useAmbientAppointmentsData = () => {
  // Atoms
  const search = useAtomValue(searchAtom);
  const selectDate = useAtomValue(selectDateAtom);

  // Debounced search
  const { debouncedValue: debouncedSearch } = useDebounce(search);

  // Date range - simple approach that works with existing API
  const dateRange = useMemo(() => {
    const selectedDate = selectDate || new Date();
    const isSearchValid = debouncedSearch.length > 2;

    if (isSearchValid) {
      return {
        startDate: undefined,
        endDate: undefined,
      };
    }

    // Use selected date as start, add 1 day as end to limit to just that day
    const startDate = startOfDay(new Date(selectedDate));
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + 1);

    return {
      startDate: startDate,
      endDate: endDate,
    };
  }, [selectDate, debouncedSearch]);

  const isUUIDv7 = (uuid: string): boolean => {
    const uuidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(uuid);
  };

  const isStandalonePatientVisit = useCallback(
    (
      appointment:
        | VoiceNotesSearchStandaloneVisit
        | VoiceNotesSearchUnifiedAppointment,
    ): appointment is VoiceNotesSearchStandaloneVisit => {
      return typeof appointment.id === 'string' && isUUIDv7(appointment.id);
    },
    [],
  );

  const isUnifiedAppointment = (
    appointment:
      | VoiceNotesSearchUnifiedAppointment
      | VoiceNotesSearchStandaloneVisit,
  ): appointment is VoiceNotesSearchUnifiedAppointment => {
    return !isStandalonePatientVisit(appointment);
  };

  const queryClient = useQueryClient();
  // Callback method to invalidate the notes cache
  const invalidateNotesCache = () => {
    queryClient.invalidateQueries([
      endpoints.ambient.voiceNotes.combinedAppointmentsList,
      endpoints.ambient.voiceNotes.batchNotesByStandalonePatientVisitIds,
      endpoints.ambient.voiceNotes.batchNotesByUnifiedAppointmentIds,
    ]);
  };

  // Fetch combined appointments (unified appointments + standalone patient visits)
  const {
    data: combinedAppointmentsData,
    isLoading,
    isError,
    hasNextPage,
    fetchNextPage,
  } = useCombinedAppointments({
    appointmentOpts: {
      startDate: dateRange.startDate?.toISOString(),
      endDate: dateRange.endDate?.toISOString(),
    },
    patientOpts: {
      name: debouncedSearch || undefined,
    },
    meta: {
      sort: {
        appointmentOn: 'ASC',
      },
      pagination: {
        limit: 10,
      },
    },
  });

  // Fetch treatments data
  const unifiedAppointmentsImagingData: BatchFindingForVoiceNotes[] =
    combinedAppointmentsData?.pages.flatMap((page) =>
      page.appointments.filter(isUnifiedAppointment).map((appointment) => ({
        unifiedAppointmentId: Number(appointment.id),
        imagingPatientId: appointment.imagingPatients[0]?.id ?? '',
      })),
    ) ?? [];

  const claimSummariesTreatmentData = useTreatmentsBatch(
    unifiedAppointmentsImagingData,
  );

  // Process treatment data
  const claimSummariesTreatments = useMemo(() => {
    const allTreatments: TBatchFindingsResponse['appointmentData'] = [];

    claimSummariesTreatmentData?.forEach((queryResult) => {
      if (queryResult.data) {
        allTreatments.push(...queryResult.data);
      }
    });

    return allTreatments;
  }, [claimSummariesTreatmentData]);

  // Process appointments data
  const appointments = useMemo(() => {
    if (!combinedAppointmentsData?.pages) return [];

    const allAppointments = combinedAppointmentsData.pages.flatMap(
      (page) => page.appointments || [],
    );

    return allAppointments;
  }, [combinedAppointmentsData]);

  // Extract unified appointment IDs for notes batch request
  const unifiedAppointmentIds = useMemo(() => {
    return appointments
      .map((appointment) => appointment.id)
      .filter((id) => !isUUIDv7(id));
  }, [appointments]);

  // Extract standalone patient visit IDs for notes batch request
  const standalonePatientVisitIds = useMemo(() => {
    return appointments
      .map((appointment) => appointment.id)
      .filter((id) => isUUIDv7(id));
  }, [appointments]);

  // Fetch notes data
  const voiceNotesForUnifiedAppointments = useBatchNotesByUnifiedAppointmentIds(
    unifiedAppointmentIds,
    !!unifiedAppointmentIds.length,
  );

  const voiceNotesForStandalonePatientVisits =
    useBatchNotesByStandalonePatientVisitIds(
      standalonePatientVisitIds,
      !!standalonePatientVisitIds.length,
    );

  // Process notes data - combine all batch results into a single map
  const processedVoiceNotesForUnifiedAppointments = useMemo(() => {
    return (
      voiceNotesForUnifiedAppointments?.reduce(
        (allNotes, queryResult) => {
          // Extract appointments from this batch result
          const batchVoiceNotesForUnifiedAppointments =
            queryResult.data?.appointments;

          // If batch has appointments, merge them into our accumulated map
          // If no appointments, return the accumulated map unchanged
          return batchVoiceNotesForUnifiedAppointments
            ? { ...allNotes, ...batchVoiceNotesForUnifiedAppointments }
            : allNotes;
        },
        {} as Record<string, ClinicalNote[]>,
      ) || {}
    );
  }, [voiceNotesForUnifiedAppointments]);

  // Process standalone patient visit notes data
  const processedVoiceNotesForStandalonePatientVisits = useMemo(() => {
    return (
      voiceNotesForStandalonePatientVisits?.reduce(
        (allNotes, queryResult) => {
          // Extract patient visits from this batch result (standalone uses 'patientVisits' key)
          const batchVoiceNotesForStandalonePatientVisits =
            queryResult.data?.patientVisits;

          // If batch has patient visits, merge them into our accumulated map
          // If no patient visits, return the accumulated map unchanged
          return batchVoiceNotesForStandalonePatientVisits
            ? { ...allNotes, ...batchVoiceNotesForStandalonePatientVisits }
            : allNotes;
        },
        {} as Record<string, ClinicalNote[]>,
      ) || {}
    );
  }, [voiceNotesForStandalonePatientVisits]);

  const appointmentsWithTreatmentsAndNotes = useMemo(() => {
    return appointments.map((appointment) => {
      const appointmentId = appointment.id;
      const isStandalone = isStandalonePatientVisit(appointment);

      // Get treatments
      const treatments = isStandalonePatientVisit(appointment)
        ? ([] as TTreatmentResponse[]) // Standalone visits don't have treatments
        : claimSummariesTreatments?.find(
            (treatmentData) =>
              treatmentData?.unifiedAppointmentId === parseInt(appointmentId),
          )?.treatments || [];

      // Get notes for this appointment
      const notes = isStandalone
        ? processedVoiceNotesForStandalonePatientVisits[appointmentId] || []
        : processedVoiceNotesForUnifiedAppointments[appointmentId] || [];

      const appointmentOnDateTime = new Date(appointment.appointmentOn);

      // Get patient info
      const patient = isStandalone
        ? {
            firstName: appointment.firstName,
            lastName: appointment.lastName,
            dob: appointment.dateOfBirth ?? undefined,
            sourcePatientId: appointment.sourcePatientId,
          }
        : appointment.pmsPatients.length > 0
          ? {
              firstName: appointment.pmsPatients[0].firstName,
              lastName: appointment.pmsPatients[0].lastName,
              dob: appointment.pmsPatients[0].dateOfBirth,
              pmsPatientId: appointment.pmsPatients[0].id,
            }
          : appointment.imagingPatients.length > 0
            ? {
                firstName:
                  appointment.imagingPatients[0].name?.split(' ')[0] ?? '',
                lastName:
                  appointment.imagingPatients[0].name?.split(' ')[1] ?? '',
                dob: appointment.imagingPatients[0].dateOfBirth,
              }
            : { firstName: '', lastName: '', dob: undefined };

      return {
        // Transform standalone visit to match appointment structure
        appointment: {
          unifiedId: !isStandalone ? appointmentId : '', // Empty for standalone visits - use standalonePatientVisitId instead
          standalonePatientVisitId: isStandalone ? appointmentId : '', // Use the standalone visit ID
          start: appointmentOnDateTime.toISOString(),
          appointmentOn: appointmentOnDateTime.toISOString(),
        },
        lastTreatments: isStandalone
          ? {
              lastBw: null,
              lastFmx: null,
              lastPerioChart: null,
              lastPerioMaintenance: null,
              lastProphy: null,
              lastSrp: null,
            }
          : {
              lastBw: null,
              lastFmx: null,
              lastPerioChart: null,
              lastPerioMaintenance: null,
              lastProphy: null,
              lastSrp: null,
            },
        patient,
        treatments,
        notes,
        isStandalone: isStandalone,
      };
    });
  }, [
    appointments,
    claimSummariesTreatments,
    isStandalonePatientVisit,
    processedVoiceNotesForStandalonePatientVisits,
    processedVoiceNotesForUnifiedAppointments,
  ]);

  // Detect if any writeback is in progress
  const isWritebackInProgress = useMemo(() => {
    if (!voiceNotesForUnifiedAppointments?.length) return false;

    // Check all batches for writeback status
    return voiceNotesForUnifiedAppointments.some((batchResult) => {
      if (!batchResult.data?.appointments) return false;

      const allNotes = Object.values(batchResult.data.appointments).flat();
      return allNotes.some(
        (note) =>
          note.writebackStatus?.clinicalNoteId &&
          !note.writebackStatus?.processedAt &&
          note.writebackStatus?.errorCode === null,
      );
    });
  }, [voiceNotesForUnifiedAppointments]);

  const isAnyBatchLoading =
    appointments.length > 0
      ? claimSummariesTreatmentData?.some(
          (queryResult) => queryResult.isLoading,
        ) ||
        voiceNotesForStandalonePatientVisits.some(
          (queryResult) => queryResult.isLoading,
        ) ||
        voiceNotesForUnifiedAppointments.some(
          (queryResult) => queryResult.isLoading,
        )
      : false;

  // Poll for updates when writeback is in progress
  useEffect(() => {
    if (isWritebackInProgress && voiceNotesForUnifiedAppointments?.length) {
      const interval = setInterval(() => {
        // Refetch all batches to ensure we get updates from any batch
        voiceNotesForUnifiedAppointments.forEach((batchResult) => {
          batchResult.refetch();
        });
      }, 10 * 1000); // Poll every 10 seconds

      return () => clearInterval(interval);
    }
  }, [isWritebackInProgress, voiceNotesForUnifiedAppointments]);

  return {
    appointmentsWithTreatmentsAndNotes,
    isLoading,
    isError,
    hasNextPage,
    fetchNextPage,
    isAnyBatchLoading,
    invalidateNotesCache,
  };
};
